import type { DateChangeDecisionBody, ProjectMeetingsResponse } from '@pulse/contracts';
import * as React from 'react';
import { Link, useParams } from 'react-router-dom';

import { AsOfPill } from '@/components/pulse/day';
import { PageHeader } from '@/components/pulse/page-header';
import { useDecideDateChange, useProjectMeetings } from '@/domains/meetings/api';
import { ProjectTabs } from '@/domains/projects/project-tab';
import { useShell } from '@/domains/shell/api';
import { ApiRequestError } from '@/lib/api-client';

/** A project's Meetings tab (MT-4), built from the prototype's `viewProjectMeetings()`. */
export function ProjectMeetingsPage(): React.ReactElement {
  const { productSlug = '' } = useParams();
  const page = useProjectMeetings(productSlug);
  const shell = useShell();
  const decide = useDecideDateChange();
  const error = decide.error instanceof ApiRequestError ? decide.error.toUserMessage() : decide.error instanceof Error ? decide.error.message : null;
  const name = page.data?.product.name ?? 'Meetings';
  return (
    <>
      <PageHeader
        eyebrow="Project · meetings"
        title={name}
        sub={`What each followed meeting said about ${name}, from Google Meet's Notes by Gemini. Latest first.`}
        right={
          <>
            <Link className="btn" to="/projects/meetings" style={{ textDecoration: 'none' }}>
              Meetings we follow
            </Link>
            <AsOfPill label={shell.data?.freshness.asOfLabel ?? null} />
          </>
        }
      />
      {page.isError ? <span className="sub">There is no project by that name.</span> : null}
      {page.data !== undefined ? <ProjectMeetingsView data={page.data} onDecide={(body) => decide.mutate(body)} busy={decide.isPending} error={error} /> : null}
    </>
  );
}

export function ProjectMeetingsView({
  data,
  onDecide,
  busy,
  error,
}: {
  readonly data: ProjectMeetingsResponse;
  readonly onDecide: (body: DateChangeDecisionBody) => void;
  readonly busy: boolean;
  readonly error: string | null;
}): React.ReactElement {
  const { product } = data;
  return (
    <>
      <ProjectTabs slug={product.slug} hasBoard={product.hasBoard} hasStories={product.hasStories} view="meetings" />
      {error !== null ? (
        <span role="alert" style={{ fontSize: 13, color: 'var(--bad-ink)' }}>
          {error}
        </span>
      ) : null}
      {data.meetings.length === 0 ? (
        <section className="card pad">
          <span style={{ color: 'var(--muted)' }}>
            No followed meeting has talked about {product.name} yet. <Link to="/projects/meetings">Meetings we follow</Link>
          </span>
        </section>
      ) : null}
      {data.meetings.map((meeting) => (
        <section key={`${meeting.meetingId}|${meeting.occurrenceId}`} className="card pad stack" style={{ gap: 12 }}>
          <div className="row" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <h2>
              {meeting.title} <span style={{ fontWeight: 400, color: 'var(--muted)' }}>· {meeting.whenLabel}</span>
            </h2>
            <span className="chip c-kind">{meeting.kind}</span>
          </div>
          <span style={{ fontSize: 13, color: 'var(--muted)' }}>{meeting.attendees.join(', ')}</span>
          {meeting.unsplit ? (
            <span style={{ color: 'var(--muted)' }}>This meeting covers several projects, and Talos hasn&apos;t split its notes by project yet.</span>
          ) : (
            <>
              <div className="two" style={{ gridTemplateColumns: 'minmax(0,1fr) 320px', gap: 20, alignItems: 'start' }}>
                <div className="stack" style={{ gap: 8 }}>
                  <span className="eyebrow">Summary</span>
                  <span style={{ color: 'var(--ink-2)' }}>{meeting.summary}</span>
                </div>
                <div className="stack" style={{ gap: 8 }}>
                  <span className="eyebrow">Next steps</span>
                  {meeting.nextSteps.length > 0 ? (
                    <ul className="plist">
                      {meeting.nextSteps.map((step) => (
                        <li key={step}>{step}</li>
                      ))}
                    </ul>
                  ) : (
                    <span style={{ color: 'var(--muted)' }}>None for {product.name}.</span>
                  )}
                </div>
              </div>
              {meeting.talos !== null ? (
                <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>
                  <b style={{ fontWeight: 600 }}>Talos:</b> {meeting.talos}
                </span>
              ) : null}
            </>
          )}
          {meeting.change !== null ? (
            meeting.change.decision === 'pending' ? (
              <div className="note" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
                <span>
                  <b style={{ fontWeight: 600 }}>Date change heard:</b> {meeting.change.milestoneTitle} from {meeting.change.fromLabel} to {meeting.change.toLabel}. It isn&apos;t applied until
                  someone accepts it.
                </span>
                <span className="row" style={{ gap: 8 }}>
                  <button type="button" className="btn primary" disabled={busy} onClick={() => onDecide({ meetingId: meeting.meetingId, occurrenceId: meeting.occurrenceId, project: product.slug, decision: 'accept' })}>
                    Accept the new date
                  </button>
                  <button type="button" className="btn" disabled={busy} onClick={() => onDecide({ meetingId: meeting.meetingId, occurrenceId: meeting.occurrenceId, project: product.slug, decision: 'dismiss' })}>
                    Keep {meeting.change.fromLabel}
                  </button>
                </span>
              </div>
            ) : (
              <div className="note">
                <span style={{ fontWeight: 600 }}>{meeting.change.decision === 'accepted' ? 'Accepted:' : 'Dismissed:'}</span>
                <span>
                  {meeting.change.milestoneTitle} {meeting.change.decision === 'accepted' ? `is now due ${meeting.change.toLabel}` : `stays due ${meeting.change.fromLabel}`}.
                </span>
              </div>
            )
          ) : null}
        </section>
      ))}
    </>
  );
}
